import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Store, Truck, Shield, CreditCard, CheckCircle, Star, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { useCart } from '@/lib/cart-context';
import { useAuth } from '@/lib/auth-context';
import { useCurrency } from '@/lib/currency-context';
import { useWishlist } from '@/lib/wishlist-context';
import { products as productsApi } from '@/lib/api';
import { computeCartTaxFromItems, productPriceCurrency } from '@/lib/productTax';
import { getCartShippingTRY, subtotalToTRY, guessShippingDestination } from '@/lib/shippingQuote';
import { buildPathForLocale, getExplicitLocaleFromPathname, buildCheckoutPath } from '@/lib/intl-seo';
import { buildProductPath } from '@/lib/product-url';
import { applyProductTranslation, getApiLangParam, getProductLangKey } from '@/lib/product-i18n';
import { ProductThumbnail } from '@/components/products/ProductThumbnail';
import { formatSelectedVariant, variantKey } from '@/lib/variant';

const CartPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const pageLang = useMemo(() => {
    const fromPath = getExplicitLocaleFromPathname(location.pathname);
    if (fromPath) return fromPath;
    return (i18n.language || 'en').substring(0, 2);
  }, [location.pathname, i18n.language]);
  const ct = useCallback((key) => t(`cart.${key}`, { lng: pageLang }), [t, pageLang]);
  const lc = pageLang;
  const langKey = getProductLangKey(pageLang);
  const productsPath = buildPathForLocale(lc, ['products']);
  const checkoutPath = buildCheckoutPath();
  const productPath = (productOrId) => buildProductPath(lc, productOrId);
  const { items, updateItem, removeItem, getWholesalePrice, loading } = useCart();
  const { user } = useAuth();
  const { formatPrice, currency, convertPrice, exchangeRates, countryCode } = useCurrency();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const navigate = useNavigate();
  const [recommendedProducts, setRecommendedProducts] = useState([]);

  const shippingDestination = useMemo(
    () => guessShippingDestination({ currency, countryCode }),
    [currency, countryCode],
  );

  const localizedItems = useMemo(
    () => items.map((item) => ({
      ...item,
      product: item.product ? applyProductTranslation(item.product, langKey) : item.product,
    })),
    [items, langKey],
  );

  // Fetch recommended products for "You might also like" section
  useEffect(() => {
    const fetchRecommendedProducts = async () => {
      try {
        const response = await productsApi.list({ limit: 100, lang: getApiLangParam(pageLang) });
        const allProducts = response.data?.products || [];
        
        // Filter out products already in cart
        const cartProductIds = items.map(item => item.product_id);
        const availableProducts = allProducts.filter(p => !cartProductIds.includes(p.id));
        
        // De-duplicate similar products
        const seen = new Set();
        const uniqueProducts = availableProducts.filter(product => {
          if (!product?.name) return false;
          const baseName = product.name
            .toLowerCase()
            .replace(/\d+[,.]?\d*\s*(mm|cm|m|kg|gr|g|ml|l|adet)?\b/gi, '')
            .replace(/[-_®™]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .substring(0, 15);
          if (seen.has(baseName)) return false;
          seen.add(baseName);
          return true;
        });
        
        // Group by seller for balanced selection
        const sellerProducts = {};
        uniqueProducts.forEach(product => {
          const sellerId = product.seller_id || product.seller_name || 'default';
          if (!sellerProducts[sellerId]) {
            sellerProducts[sellerId] = [];
          }
          sellerProducts[sellerId].push(product);
        });
        
        // Round-robin selection: 2 products from each seller
        const recommended = [];
        const sellers = Object.keys(sellerProducts);
        for (let i = 0; i < 2 && recommended.length < 16; i++) {
          for (const seller of sellers) {
            if (sellerProducts[seller][i] && recommended.length < 16) {
              recommended.push(sellerProducts[seller][i]);
            }
          }
        }
        
        // If still not enough, fill with remaining unique products
        if (recommended.length < 16) {
          const remaining = uniqueProducts.filter(p => !recommended.includes(p));
          recommended.push(...remaining.slice(0, 16 - recommended.length));
        }
        
        setRecommendedProducts(recommended.slice(0, 16));
      } catch (error) {
        console.error('Failed to fetch recommended products:', error);
      }
    };
    
    fetchRecommendedProducts();
  }, [items, pageLang]);

  // Toplam ağırlık hesaplama (kg cinsinden)
  const calculateTotalWeight = () => {
    let totalWeightGrams = 0;
    items.forEach(item => {
      const weightGrams = item.product?.weight || 100;
      totalWeightGrams += weightGrams * item.quantity;
    });
    return totalWeightGrams / 1000;
  };

  const totalWeightKg = calculateTotalWeight();

  const cartTax = useMemo(
    () => computeCartTaxFromItems(items, getWholesalePrice, convertPrice),
    [items, getWholesalePrice, convertPrice]
  );

  const shippingTry = useMemo(() => {
    const subTRY = subtotalToTRY(cartTax.subtotal, currency, exchangeRates);
    return getCartShippingTRY({
      items,
      destinationCountry: shippingDestination,
      subtotalTRY: subTRY,
      totalWeightKg,
    });
  }, [items, cartTax.subtotal, currency, exchangeRates, totalWeightKg, shippingDestination]);

  const convertedTotalPrice = cartTax.subtotal;
  const tax = cartTax.totalVatDisplay;
  const convertedShipping = convertPrice(shippingTry, 'TRY');
  const total = cartTax.grandTotal + convertedShipping;

  const handleCheckout = () => {
    navigate(checkoutPath);
  };

  if (items.length === 0) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="min-h-[40vh] flex items-center justify-center" data-testid="cart-empty">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <ShoppingBag className="w-12 h-12 text-gray-300" />
            </div>
            <h1 className="text-lg font-medium text-gray-900 mb-1">{ct('empty')}</h1>
            <p className="text-sm text-gray-500 mb-6">{ct('emptySubtitle')}</p>
            <Link to={productsPath}>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded px-6" data-testid="continue-shopping-btn">
                {ct('startShopping')}
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Bunları da Beğeneceksiniz - Empty Cart */}
        {recommendedProducts.length > 0 && (
          <div className="container mx-auto px-4 max-w-6xl pb-8">
            <div className="bg-white rounded-lg shadow-sm p-4" data-testid="recommended-products">
              <h2 className="text-base font-medium mb-4">{ct('youMayAlsoLike')}</h2>
              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                {recommendedProducts.map((product) => (
                  <Link
                    key={product.id}
                    to={productPath(product)}
                    className="group bg-white rounded-lg border hover:shadow-md transition-all overflow-hidden"
                  >
                    <div className="relative aspect-square overflow-hidden bg-gray-100">
                      <ProductThumbnail
                        src={product.images?.[0]}
                        alt={product.name}
                        className="h-full w-full"
                        coverImgClassName="group-hover:scale-105 transition-transform"
                      />
                      <button className="absolute top-1 right-1 w-5 h-5 bg-white/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Heart className="w-3 h-3 text-gray-600" />
                      </button>
                      {product.seller_info?.country && (
                        <div className="absolute bottom-1 right-1 bg-white/90 px-1 py-0.5 rounded text-xs">
                          {(() => {
                            const code = product.seller_info.country;
                            const codePoints = code.toUpperCase().split('').map(c => 127397 + c.charCodeAt());
                            return String.fromCodePoint(...codePoints);
                          })()}
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <h3 className="text-xs font-medium text-gray-900 line-clamp-2 min-h-[2rem]">{product.name}</h3>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Star className="w-2.5 h-2.5 fill-yellow-400 text-yellow-400" />
                        <span className="text-[10px] text-gray-500">{product.rating || '4.5'}</span>
                      </div>
                      <div className="mt-1">
                        <span className="font-bold text-sm text-orange-600">{formatPrice(product.price, productPriceCurrency(product))}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen py-4" data-testid="cart-page">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-lg font-medium text-gray-900">
            {ct('myCart')} <span className="text-gray-500 font-normal">({items.length} {ct('items')})</span>
          </h1>
          <Link to={productsPath} className="text-sm text-orange-500 hover:underline">
            {ct('continueShopping')}
          </Link>
        </div>

        <div className="grid lg:grid-cols-12 gap-4">
          {/* Cart Items */}
          <div className="lg:col-span-8">
            {/* Seller Group - All items */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Seller Header */}
              <div className="flex items-center gap-3 p-3 border-b bg-gray-50">
                <Checkbox checked={true} className="w-4 h-4" />
                <Store className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium">Turkish E-Market</span>
                <span className="text-xs text-gray-400">({localizedItems.length} {ct('items')})</span>
              </div>

              {/* Items */}
              <div className="divide-y">
                {localizedItems.map((item) => (
                  <div key={`${item.product_id}-${variantKey(item.selected_variant)}`} className="p-3 hover:bg-gray-50" data-testid={`cart-item-${item.product_id}`}>
                    <div className="flex gap-3">
                      {/* Checkbox */}
                      <Checkbox checked={true} className="w-4 h-4 mt-1" />
                      
                      {/* Image */}
                      <Link to={productPath(item.product || item.product_id)} className="w-16 h-16 flex-shrink-0 block overflow-hidden rounded">
                        <ProductThumbnail
                          src={item.product?.images?.[0]}
                          alt={item.product?.name ?? ''}
                          className="h-full w-full"
                        />
                      </Link>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <Link to={productPath(item.product || item.product_id)}>
                          <h3 className="text-sm text-gray-900 line-clamp-2 hover:text-orange-500">
                            {item.product?.name}
                          </h3>
                        </Link>
                        {item.product?.brand && (
                          <p className="text-xs text-gray-500 mt-0.5">{item.product.brand}</p>
                        )}
                        {formatSelectedVariant(item.selected_variant) && (
                          <p className="text-xs font-medium text-blue-700 mt-0.5">
                            {formatSelectedVariant(item.selected_variant)}
                          </p>
                        )}
                        
                        {/* Shipping Info */}
                        <div className="flex items-center gap-1 mt-1">
                          <Truck className="w-3 h-3 text-green-600" />
                          <span className="text-xs text-green-600">{ct('estimatedDelivery')}</span>
                        </div>
                      </div>

                      {/* Price & Actions */}
                      <div className="flex flex-col items-end gap-2">
                        {/* Price */}
                        <div className="text-right">
                          {(() => {
                            const regularPrice = item.product?.price || 0;
                            const productCurrency = productPriceCurrency(item.product);
                            const wholesalePrice = getWholesalePrice(item.product, item.quantity);
                            const hasDiscount = wholesalePrice < regularPrice;
                            return hasDiscount ? (
                              <>
                                <p className="text-xs text-gray-400 line-through">{formatPrice(regularPrice, productCurrency)}</p>
                                <p className="text-sm font-bold text-orange-600">{formatPrice(wholesalePrice, productCurrency)}</p>
                              </>
                            ) : (
                              <p className="text-sm font-bold text-orange-600">{formatPrice(regularPrice, productCurrency)}</p>
                            );
                          })()}
                        </div>

                        {/* Quantity */}
                        <div className="flex items-center border rounded">
                          <button
                            className="w-7 h-7 flex items-center justify-center text-gray-500 hover:bg-gray-100"
                            onClick={() => updateItem(item.product_id, item.quantity - 1, item.selected_variant)}
                            data-testid={`qty-minus-${item.product_id}`}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <button
                            className="w-7 h-7 flex items-center justify-center text-gray-500 hover:bg-gray-100"
                            onClick={() => updateItem(item.product_id, item.quantity + 1, item.selected_variant)}
                            data-testid={`qty-plus-${item.product_id}`}
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>

                        {/* Remove */}
                        <div className="flex items-center gap-2">
                          <button
                            className={`text-xs flex items-center gap-1 transition-colors ${
                              isInWishlist(item.product_id)
                                ? 'text-red-500 hover:text-red-600'
                                : 'text-gray-400 hover:text-red-500'
                            }`}
                            onClick={() => toggleWishlist(item.product_id, item.product?.name)}
                            data-testid={`wishlist-toggle-${item.product_id}`}
                          >
                            <Heart className={`w-3.5 h-3.5 ${isInWishlist(item.product_id) ? 'fill-red-500' : ''}`} />
                          </button>
                          <button
                            className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1"
                            onClick={() => removeItem(item.product_id, item.selected_variant)}
                            data-testid={`remove-${item.product_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                            {ct('remove')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary - Sidebar */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-lg shadow-sm p-4 sticky top-4">
              {/* Title */}
              <h2 className="text-base font-medium mb-4">{ct('orderSummary')}</h2>
              
              {/* Price Breakdown */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{ct('subtotal')}</span>
                  <span>{formatPrice(convertedTotalPrice, currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{ct('shipping')}</span>
                  <span>{formatPrice(convertedShipping, currency)}</span>
                </div>
                <p className="text-[11px] text-gray-400 -mt-1">
                  {shippingDestination === 'TR' ? ct('shippingDomestic') : ct('shippingInternational')}
                </p>
                <div className="flex justify-between">
                  <span className="text-gray-600">{ct('tax')}</span>
                  <span>{formatPrice(tax, currency)}</span>
                </div>
              </div>

              <Separator className="my-3" />

              {/* Total */}
              <div className="flex justify-between items-center mb-4">
                <span className="font-medium">{ct('total')}</span>
                <span className="text-xl font-bold text-orange-600">{formatPrice(total, currency)}</span>
              </div>

              {/* Weight Info */}
              <p className="text-xs text-gray-500 mb-3">
                📦 {totalWeightKg.toFixed(2)} kg • {ct('shippingNote')}
              </p>

              {/* Checkout Button */}
              <Button 
                className="w-full bg-orange-500 hover:bg-orange-600 text-white h-11 text-sm font-medium"
                onClick={handleCheckout}
                data-testid="checkout-btn"
              >
                {ct('proceedToCheckout')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              {/* Trust Badges */}
              <div className="mt-4 pt-3 border-t space-y-2">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>{ct('securePayment')}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                  <span>{ct('returnGuarantee')}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <CreditCard className="w-4 h-4 text-purple-600" />
                  <span>{ct('installmentOption')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bunları da Beğeneceksiniz - 8 ürün x 2 satır = 16 ürün */}
        {recommendedProducts.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow-sm p-4" data-testid="recommended-products">
            <h2 className="text-base font-medium mb-4">{ct('youMayAlsoLike')}</h2>
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
              {recommendedProducts.map((product) => (
                <Link
                  key={product.id}
                  to={productPath(product)}
                  className="group bg-white rounded-lg border hover:shadow-md transition-all overflow-hidden"
                >
                  <div className="relative aspect-square overflow-hidden bg-gray-100">
                    <ProductThumbnail
                      src={product.images?.[0]}
                      alt={product.name}
                      className="h-full w-full"
                      coverImgClassName="group-hover:scale-105 transition-transform"
                    />
                    {/* Heart button */}
                    <button className="absolute top-1 right-1 w-5 h-5 bg-white/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Heart className="w-3 h-3 text-gray-600" />
                    </button>
                    {/* Seller country flag */}
                    {product.seller_info?.country && (
                      <div className="absolute bottom-1 right-1 bg-white/90 px-1 py-0.5 rounded text-xs">
                        {(() => {
                          const code = product.seller_info.country;
                          const codePoints = code.toUpperCase().split('').map(c => 127397 + c.charCodeAt());
                          return String.fromCodePoint(...codePoints);
                        })()}
                      </div>
                    )}
                  </div>
                  <div className="p-2">
                    <h3 className="text-xs font-medium text-gray-900 line-clamp-2 min-h-[2rem]">{product.name}</h3>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Star className="w-2.5 h-2.5 fill-yellow-400 text-yellow-400" />
                      <span className="text-[10px] text-gray-500">{product.rating || '4.5'}</span>
                    </div>
                    <div className="mt-1">
                      <span className="font-bold text-sm text-orange-600">{formatPrice(product.price, productPriceCurrency(product))}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
